import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { calcPointsRedeem, usePointsSummary, type PointsRedeemResult } from "@utils/points";
import { lineTotalOf } from "./couponDiscount";

export interface CartPointsRedeem {
  /** 依當下金額 clamp 後的實際折抵結果 */
  result: PointsRedeemResult;
  /** 此帳戶的點數餘額 */
  balance: { storedGas: number; account: number };
  /** 餘額載入中 */
  loading: boolean;
  /** preview 已回、折抵上限算得準（false 時不要顯示折抵金額，會先閃錯的數字） */
  ready: boolean;
  /** 帳戶完全沒有任何點數 */
  empty: boolean;
}

/**
 * 購物車的點數折抵計算。折抵區塊與訂單摘要共用這支，兩邊的數字才不會各算各的。
 *
 * 基準：後端 preview 的 total（券後、含各項費用）；存氣點數另外受「券後瓦斯小計」限制。
 */
export const useCartPointsRedeem = (): CartPointsRedeem => {
  const { productList, couponDiscount, previewTotal, pointsRedeem } = useOrder();
  const { chooseIdentity } = useUser();
  const { balance, loading } = usePointsSummary(chooseIdentity?.context_id ? String(chooseIdentity.context_id) : null);

  const gasSum = (productList ?? []).reduce((sum, item) => sum + (item.gas_type ? lineTotalOf(item) : 0), 0);
  const appliedCoupon = Math.min(Math.max(0, couponDiscount ?? 0), gasSum);

  const result = calcPointsRedeem({
    gasSubtotalAfterCoupon: gasSum - appliedCoupon,
    payableAfterCoupon: previewTotal ?? 0,
    balance,
    wantStoredGas: pointsRedeem.storedGas,
    wantAccount: pointsRedeem.account,
  });

  return {
    result,
    balance,
    loading,
    ready: previewTotal !== null && couponDiscount !== null,
    empty: !loading && balance.storedGas <= 0 && balance.account <= 0,
  };
};
